import { CardContent, Grid, Pagination, Typography } from "@mui/material";
import { APP_PAGE_SIZE_DEFAULT } from "app/appConstants";
import { CorePermissions } from "app/appPermissions";
import { MprcCard, MprcCardActions, MprcClickable, MprcConfirmDialog, MprcContainer, MprcIconButtonMoreOptionsDropdown, MprcJoinComponents, MprcRequestErrorDialog, MprcRequestErrorReport, MprcRequestIndicator, MprcSpinnerIndicator, MprcText } from "components";
import { MprcFilterCount } from "components/filter";
import { MprcDropdownBuildGroupedItems, MprcDropdownItems } from "components/MprcIconButtonDropdown";
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { EnrolledUsersOption, ManagedClassroomsListResponse, ManagedClassroomsListResponsePaginatedList, ManageEnrollOperation, SupervisorTrainingsQueryMode, TrainingBasicInfoResponse } from "generated/CoreApi_v1";
import { usePageTitle } from "hooks/usePageTitle";
import HtmlUtils from "lib/HtmlUtils";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { LmsPaths } from "routes/LmsRouter";
import { ITrainingPanelSupervisorDashboardProps } from "views/dashboard/TrainingPanel/TrainingPanelSupervisorDashboard";
import { NewDirectMessageDialog } from "views/foundation/directMessage/NewDirectMessageDialog";
import { useClassroomManagementContext } from "views/lms/classroom/Home/ClassroomManagementWrapper";
import { Filter } from "views/lms/classroom/Home/ListClassrooms/ListClassroomFilter";
import { ListClassroomHeader } from "views/lms/classroom/Home/ListClassrooms/ListClassroomHeader";
import { TrainingRegistrationDialog } from "views/lms/student/Registration/TrainingRegistrationDialog";
import { TrainingAudienceListUsersDrawer } from "views/lms/training/components/TrainingAudienceListUsersDrawer";
import { TrainingListUsersDrawer } from "views/lms/training/components/TrainingListUsersDrawer";

////////////////////////////////////////////////////////////////////////////

export const ListClassrooms: React.FC = () => {

    const ctx = useClassroomManagementContext();
    const api = useCoreApi();
    const navigate = useNavigate();
    const { trainingId, trainingType } = useParams();
    const session = useSession();

    const { t } = useTranslation("lms");

    const [dialog, setDialog] = useState<any>();
    const [viewData, setViewData] = useState<ManagedClassroomsListResponsePaginatedList | null>(null);
    const [trainingInfo, setTrainingInfo] = useState<TrainingBasicInfoResponse>();
    const [viewError, setViewError] = useState<any>();
    const [loading, setLoading] = useState<boolean>(true);
    const [deleting, setDeleting] = useState<boolean>(false);

    const filtered = useRef<boolean>(false);

    usePageTitle(t("Classroom.List.Title"));

    ////////////////////////////////////////////////////////////////////////////

    ///
    const resetFilters = () => {
        ctx.resetFilter();
    };

    ///
    const handleEditClassroom = (classroomId: string | null) => {
        navigate(LmsPaths.BuildEditClassroom(trainingId!, trainingType!, classroomId));
    };

    type MenuOptions = "enrollUser" | "notifyEnrollments" | "waitingList" | "infoPanel" | "monitoring" | "remove" | "preview" | "audience" | "generalInfo";

    ///
    async function deleteClassroom(classroomId: string) {
        setDeleting(true);
        try {
            var result = await api.v1.classroomManagement.deleteClassroom({ classroomId });
            if (result.data) {
                if (viewData?.items.length == 1 && ctx.pagination.page > 1) ctx.setPagination({ page: ctx.pagination.page - 1 });
                else loadClassrooms();
            }
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setDeleting(false);
        }
    }

    ///
    async function loadClassrooms() {
        setLoading(true);

        try {
            filtered.current = (ctx.filter.searchText?.length || 0) > 0;
            if (trainingId) {
                if (trainingInfo == undefined) {
                    const response = await api.v1.trainingManagement.getTrainingBasicInfo({ trainingId });
                    setTrainingInfo(response.data);
                }

                const result = await api.v1.trainingManagement.getClassrooms({
                    trainingId,
                    searchText: ctx.filter.searchText || null,
                    onlyActive: ctx.filter.onlyActive,
                    filter: ctx.filter.advFilters,
                    orderBy: ctx.filter.order,
                    pageNumber: ctx.pagination.page,
                    pageSize: APP_PAGE_SIZE_DEFAULT
                });

                setViewData(result.data);
            }
        }
        catch (e) {
            setViewData(null);
            setViewError(e);
        }
        finally {
            setLoading(false);
        }
    }

    const handleViewUsers = (item: ManagedClassroomsListResponse, option: EnrolledUsersOption) => {
        setDialog(
            <TrainingListUsersDrawer
                option={option}
                trainingId={trainingId!}
                classroomId={item.classroomId}
                subtitle={item.name}
                onClose={() => setDialog(null)}
            />
        );
    };

    ///
    const getMenuItems = useCallback(
        (item: ManagedClassroomsListResponse): MprcDropdownItems<MenuOptions> => {

            const group0: MprcDropdownItems<MenuOptions> = [];

            if (session.permissionExplorer.hasOne([CorePermissions.TrainingManageEnrollments, CorePermissions.TrainingManageEnrollmentsTargetAudience])) {
                group0.push({ text: t("Classroom.List.Overflow.EnrollUser"), value: "enrollUser" });
            }

            const group1: MprcDropdownItems<MenuOptions> = [
                { text: t("Training.List.Overflow.Audience"), value: "audience" },
                { text: t("Classroom.List.Overflow.NotifyEnrollments"), value: "notifyEnrollments" }
            ];

            const group2: MprcDropdownItems<MenuOptions> = [];
            group2.push(
                { text: t("Training.List.Overflow.Preview"), value: "preview" },
                { text: t("Training.List.Overflow.GeneralInfo"), value: "generalInfo" }
            );

            if (item.canTutoring) {
                group2.push({ text: t("Classroom.List.Overflow.Monitoring"), value: "monitoring" });
            }

            if (session.permissionExplorer.hasOne([CorePermissions.TrainingViewDashboard]))
                group2.push({ text: t("Classroom.List.Overflow.InfoPanel"), value: "infoPanel" });


            const group3: MprcDropdownItems<MenuOptions> = [
                { text: <MprcText color="error">{t("Actions.Delete", { ns: "shared" })}</MprcText>, value: "remove" }
            ];

            return MprcDropdownBuildGroupedItems<MenuOptions>([group0, group1, group2, group3]);
        },
        []
    );

    ///
    const handleSelectOption = (option: MenuOptions, item: ManagedClassroomsListResponse) => {
        switch (option) {
            case "preview":
                HtmlUtils.openUrlInNewWindow(LmsPaths.BuildEnrollmentPreview(trainingId!, item.classroomId), "TrainingPreview-" + item.classroomId);
                break;

            case "remove":
                setDialog(<MprcConfirmDialog
                    open
                    message={t("Classroom.List.ConfirmDeleteMessage")}
                    onClose={
                        (confirm) => {
                            if (confirm) deleteClassroom(item.classroomId);
                            setDialog(null);
                        }}
                />);
                break;

            case "enrollUser":
                navigate(LmsPaths.BuildEnrollmentManager(trainingId, item.classroomId, ManageEnrollOperation.Enrollment));
                break;

            case "monitoring":
                navigate(LmsPaths.BuildSupervisorClassroom(item.classroomId!));
                break;

            case "notifyEnrollments":
                setDialog(<NewDirectMessageDialog
                    title={item.name}
                    onPost={(subject, body) => {
                        return new Promise((resolve, reject) => {
                            api.v1.enrollment.sendMessageEnrollment({
                                trainingId: trainingId!,
                                classroomId: item.classroomId,
                                attachId: null,
                                subject,
                                body
                            })
                                .then((response) => resolve(response.data))
                                .catch(reject);
                        });
                    }}
                    onClose={() => setDialog(null)}
                />);
                break;
            case "infoPanel":
                navigate(LmsPaths.SupervisorTrainingPanel, {
                    state: {
                        training: { key: trainingId, value: item.trainingName },
                        classroom: { key: item.classroomId, value: item.name },
                        viewMode: SupervisorTrainingsQueryMode.Coordinator
                    } as ITrainingPanelSupervisorDashboardProps
                });
                break;
            case "audience":
                setDialog(
                    <TrainingAudienceListUsersDrawer
                        trainingId={trainingId!}
                        subtitle={item.name}
                        classroomId={item.classroomId}
                        onlyMandatory={false}
                        onClose={() => setDialog(null)}
                    />
                );
                break;
            case "generalInfo":
                setDialog(<TrainingRegistrationDialog
                    trainingId={trainingId!}
                    classroomId={item.classroomId}
                    readonly
                    onClose={() => setDialog(null)}
                />);
                break;
            default:
                //TODO: Lista de Espera não será implementado agora.
                alert("Não implementado");
                break;
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        loadClassrooms();
    }, []);

    useEffect(() => {
        loadClassrooms();
    }, [ctx.filter, ctx.pagination]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    ///
    const buildSecondLine = (item: ManagedClassroomsListResponse) => {
        let c: React.ReactNode[] = [];

        if (!item.isActive)
            c.push(<Typography variant="bodyNotice">{t("Classroom.List.EnrollmentInactiveText")}</Typography>);
        else if (item.enrollSuspended)
            c.push(<Typography variant="bodyNotice">{t("Classroom.List.EnrollSuspendedText")}</Typography>);
        else if (item.enrollClosed)
            c.push(<Typography variant="bodyNotice">{t("Classroom.List.EnrollmentCloseText")}</Typography>);
        else
            c.push(<Typography variant="caption" color="success.main">{t("Classroom.List.EnrollmentOpenText")}</Typography>);

        if (item.enrollmentsCount)
            c.push(<MprcClickable onClick={() => handleViewUsers(item, EnrolledUsersOption.Enrolled)}><Typography variant="caption">{t("Classroom.List.EnrollmentCountText", { count: item.enrollmentsCount })}</Typography></MprcClickable>);

        if (item.activeEnrollmentsCount)
            c.push(<MprcClickable onClick={() => handleViewUsers(item, EnrolledUsersOption.Active)}><Typography variant="caption">{t("Classroom.List.ActiveEnrollmentsCountText", { count: item.activeEnrollmentsCount })}</Typography></MprcClickable>);

        if (item.enrollmentsCount) {
            if (item.finishedEnrollmentCount == 0) {
                c.push(<Typography variant="caption">{t("Classroom.List.NobodyFinishedText")}</Typography>);
            } else {
                c.push(<Typography variant="caption">
                    <MprcClickable onClick={() => handleViewUsers(item, EnrolledUsersOption.Completed)}><span>{t("Classroom.List.FinishedEnrollmentText", { count: item.finishedEnrollmentCount })}</span></MprcClickable>
                    <span>{item.approvedPercent > 0 ? ` (${t("Classroom.List.ApprovedPercentText", { count: item.approvedPercent })})` : ""}</span>
                </Typography>);
            }
        }

        return <Typography variant="caption"><MprcJoinComponents components={c} separator={<span>&nbsp;&bull;&nbsp;</span>} /></Typography>;
    };

    ///
    const renderContent = () => {
        return (
            <CardContent>
                {
                    loading
                        ? <MprcSpinnerIndicator />
                        : viewData && (
                            <>
                                <MprcContainer>
                                    <MprcFilterCount
                                        loading={loading}
                                        isEmpty={viewData!.totalRows == 0}
                                        countText={t("Classroom.List.RecordsFoundText", { count: viewData!.totalRows })}
                                        filter={{
                                            active: filtered.current,
                                            text: t("Classroom.List.RecordsFilteredText", { count: viewData!.totalRows }),
                                            onResetFilter: resetFilters
                                        }}
                                    />
                                </MprcContainer>
                                {
                                    viewData!.totalRows > 0 && (
                                        <>
                                            <Grid container justifyContent="center" alignItems="center" spacing={2}>
                                                {viewData!.items.map((item, index) => {
                                                    return (
                                                        <Grid size={{ xs: 12 }} key={index} alignItems="center" justifyContent="center">
                                                            <Grid container justifyContent="flex-start" alignItems="flex-start" spacing={1} wrap="nowrap">
                                                                <Grid size="grow">
                                                                    <Grid container direction="row">
                                                                        <Grid size={{ xs: 12 }}>
                                                                            <Grid container alignItems="center" wrap="nowrap">
                                                                                <Grid>
                                                                                    <MprcListItemTitle
                                                                                        highlighted={!item.isActive}
                                                                                        onClick={() => handleEditClassroom(item.classroomId)}
                                                                                    >
                                                                                        {item.name}
                                                                                    </MprcListItemTitle>
                                                                                </Grid>
                                                                                <Grid>
                                                                                    <MprcIconButtonMoreOptionsDropdown
                                                                                        color="muted"
                                                                                        onChange={(selection) => handleSelectOption(selection, item)}
                                                                                        items={getMenuItems(item)}
                                                                                    />
                                                                                </Grid>
                                                                            </Grid>
                                                                        </Grid>
                                                                        <Grid size={{ xs: 12 }} mt={theme => theme.spacing(-.5)}>
                                                                            {buildSecondLine(item)}
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    );
                                                })
                                                }
                                            </Grid>
                                        </>
                                    )
                                }
                            </>
                        )
                }

                {!loading && viewData && viewData.totalPages > 1 && (
                    <MprcCardActions>
                        <Pagination
                            shape="rounded"
                            color="primary"
                            page={viewData.pageNumber}
                            count={viewData.totalPages}
                            onChange={(e, pageNumber) => {
                                if (pageNumber != ctx.pagination.page) {
                                    ctx.setPagination({ page: pageNumber });
                                }
                            }}
                        />
                    </MprcCardActions>
                )}
            </CardContent>
        );
    };

    ///
    return (
        <>
            {dialog}

            <MprcRequestIndicator
                visible={loading || deleting} type={loading ? "request" : "post"}
                message={deleting ? t("Classroom.List.RemovingClassroomText") : undefined}
            />

            {
                viewError && viewData != null &&
                <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />
            }

            <MprcContainer position="sticky" margin="none">
                <ListClassroomHeader />
            </MprcContainer>

            <MprcContainer>
                <MprcCard>
                    {
                        viewError && viewData == null
                            ? <MprcRequestErrorReport error={viewError} />
                            : <>
                                <Filter
                                    onAdd={() => handleEditClassroom(null)}
                                />
                                {renderContent()}
                            </>
                    }
                </MprcCard>
            </MprcContainer >
        </>
    );
};
